import { Component, h } from 'preact'
import Header from '../header'
import { InformationCallout } from '../ui-components/InformationCallout'
import { HowToTextWithOpenInNewTabIcon } from '../ui-components/HowToTextWithOpenInNewTabIcon'
import Icon from '../assets'
import ButtonControl from '../button-control'
import axios from 'axios'
import qs from 'qs'

interface Props {}

interface State {
    showUploadButton: boolean,
    fileName?: string,
    file: File | null,
    isLoading: boolean,
    showImportSuccess: boolean,
    uploadedCompaniesCount?: number | null,
    showImportError: boolean
}

class ImportView extends Component<Props, State> {
    downloadLink: string
    constructor (props: Props) {
      super(props)
      this.setState({
        showUploadButton: false,
        fileName: undefined,
        isLoading: false,
        showImportSuccess: false,
        showImportError: false
      })
      this.downloadLink = '/company/download'
    }

    openFileSelector () {
      const fileUploader: HTMLElement | null = document.querySelector('#file-selector')

      if (fileUploader) {
        fileUploader.click()
      }
    }

    async uploadFileToS3 (file: File) {
        const fileType = 'text/csv'
        const url = `/attachment/signedUrl?filename=${file.name}&content_type=${fileType}`
        const getSignedUrlResponse = await axios.get(url)

        const fileData = getSignedUrlResponse.data
        const searchQuery = window.location.search
        const queryParameters = qs.parse(searchQuery, {ignoreQueryPrefix: true})

        try {
            await axios({
                url: fileData.url,
                method: 'PUT',
                data: file,
                headers: {
                    'content-type': fileType + '; charset=utf-8',
                    'x-amz-tagging': queryParameters['x-amz-tagging'],
                    'X-Requested-With': null
                }
            })
            return getSignedUrlResponse
        } catch {
            this.setState({
                isLoading: false,
                showImportError: true
            })
        }
        return null
    }

    async uploadFile () {
      const { file } = this.state
      this.setState({
        isLoading: true
      })

      if (file) {
        const signedUrlResponse = await this.uploadFileToS3(file)
        if (signedUrlResponse === null) {
            return
        }

        const fileNameInS3 = signedUrlResponse?.data.filename

        try {
          const response = await axios.post('/company/upload',
            {
              fileName: fileNameInS3
            })
          this.setState({
            showImportSuccess: true,
            uploadedCompaniesCount: response.data.companiesImported
          })
        } catch {
          this.setState({
            showImportError: true,
            isLoading: false
          })
        }
      }
    }

    setFileName(event: Event) {
      const target = event.target as HTMLInputElement
      const uploadedFile: File = (target.files as FileList)[0]
      const uploadedFileName: string = uploadedFile.name
      this.setState({
        showUploadButton: true,
        fileName: uploadedFileName,
        file: uploadedFile
      })
    }

    removeSelectedFile (e: Event) {
        if (this.state.isLoading) {
            return
        }

        const target = e.target as HTMLInputElement
        target.value = ''
        this.setState({
            showUploadButton: false,
            fileName: undefined,
            file: null
        })
    }

    showImportView () {
        this.setState({
            showImportError: false,
            showUploadButton: false,
            fileName: undefined,
            file: null
        })
    }

    render(_: Props, {showUploadButton, fileName, isLoading, showImportError, showImportSuccess, uploadedCompaniesCount}: State) {
        return (
            <div className='company-import-view qa-company-import-view'>
                <Header
                    hasBreadcrumb
                    breadcrumbText='Company Overview'
                    breadcrumbLink='/company/overview'
                    title='Import company data'
                    disableCancelButton
                    disableSaveButton
                />
                <div className='company-import-view-container'>
                    {
                        showImportSuccess &&
                        <div className='company-message-container'>
                            <Icon customClass='icon-import-success' icon='answer' width={80} height={80}/>
                            <div className='upload-message-text'>Import complete!</div>
                            <div>
                                <div className='upload-number'> {uploadedCompaniesCount} </div>
                                <div className='upload-description'>Company created</div>
                            </div>
                            <ButtonControl variant='secondary' newStyle size='medium' title='Go back to company overview'
                                           onClick={() => window.location.assign('/company/overview?redirect=true')}/>
                        </div>
                    }
                    {
                        showImportError && <div>
                        <div className='company-message-container'>
                            <Icon customClass='icon-import-error' icon='warning' width={80} height={80}/>
                            <div className='upload-message-text'>Import failed :(</div>
                            <div className='upload-description'>Please download the <a href={this.downloadLink} className='download-link' target='_blank'>template CSV file</a> and check that your file matches the format of the template. </div>
                            <ButtonControl variant='brand' newStyle size='medium' title='Try again'
                                           onClick={() => this.showImportView()}/>
                        </div>
                        <HowToTextWithOpenInNewTabIcon label='Read more about company data importing'
                        url='https://community.insided.com/article/931?t=eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJhcnRpY2xlX2lkIjo5MzF9.DVLQzauX-P-JR1o3gNglNSuMptyU9by03hBDda5tA3M'/>
                        </div>
                    }

                {!showImportSuccess && !showImportError &&
                    <div className='company-import-data-container'>
                        <InformationCallout
                            text={`Importing a CSV allows you to add and update companies stored in inSided. If a company domain in your CSV matches an existing company, they will be updated with the mapped values. Otherwise, a company will be created.`}/>
                            <div className='company-upload-container'>
                                <Icon customClass='icon-upload' icon='upload'/>
                                <div className='upload-title-text'>Upload your CSV file</div>
                                 <div className='company-upload-info-text'> Please upload a CSV with the
                                     following file format: company email domain, company name, customer status.
                                 </div>
                                <div className='company-upload-link'>
                                <div>
                                {fileName === undefined
                                    ? <div>
                                        <ButtonControl variant='brand' newStyle size='medium' class='qa-file-selector' title=' Select file from your computer'
                                                       onClick={() => this.openFileSelector()}/>
                                       <input type='file' id='file-selector' style='display:none;' onChange={(e) => this.setFileName(e)} accept='.csv'/>
                                      </div>
                                   : <div className='upload-file-wrapper'>
                                        <span className='icon-file-name'> <Icon customClass='icon-file' icon='file'/></span>
                                        <span className='upload-file-name'>{fileName} </span>
                                         <span>(<a onClick={(e) => this.removeSelectedFile(e)}
                                                   className={`remove-file ${isLoading ? 'remove-file-disabled' : ''}`}>Remove</a>)</span>
                                      </div>}
                                {showUploadButton &&
                                    <ButtonControl variant='brand' newStyle size='medium' class='upload-file' title='Upload file' onClick={() => this.uploadFile()}
                                                               isLoading={isLoading} />}
                                </div>
                                </div>
                                {!showUploadButton && <div>
                                 <div className='company-download-wrapper'>
                                     <a href={this.downloadLink} target='_blank'><span className='icon-download-link'>
                                         <Icon customClass='icon-download' icon='download'/></span>
                                         <span className='download-link'> Download template CSV file </span>
                                     </a>
                                  </div>
                            </div>}
                     </div>
                      <HowToTextWithOpenInNewTabIcon label='Read more about company data importing'
                                                     url='https://community.insided.com/article/931?t=eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJhcnRpY2xlX2lkIjo5MzF9.DVLQzauX-P-JR1o3gNglNSuMptyU9by03hBDda5tA3M'/>
                    </div>}
                </div>
            </div>
        )
    }
}
export default ImportView
